import React, { useState, useEffect } from 'react';

const PlatformEndpointsEditor = ({ 
  endpoints = null, 
  onChange, 
  disabled = false 
}) => {
  const defaultTemplate = {
    app_integration_endpoints: [
      {
        route: 'your API Route',
        route_type: 'api',
        api_instructions: {
          header: {},
          body: {},
          api_key: 'your API key'
        },
        method: 'POST',
        service_name: 'name of service, lowercase, no spaces'
      }
    ]
  };

  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFormatted, setIsFormatted] = useState(true);

  useEffect(() => {
    console.log('PlatformEndpointsEditor received endpoints:', endpoints);
    if (endpoints) {
      try {
        const formattedJson = JSON.stringify(endpoints, null, 2);
        setJsonText(formattedJson);
        console.log('Set JSON text to:', formattedJson);
      } catch (err) {
        console.error('Error formatting endpoints:', err);
        setJsonText('{}');
      }
    } else {
      console.log('No endpoints provided, using default template');
      const formattedJson = JSON.stringify(defaultTemplate, null, 2);
      setJsonText(formattedJson);
    }
  }, [endpoints]);

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonText(value);
    setError('');

    try {
      const parsed = JSON.parse(value);
      onChange(parsed);
    } catch (err) {
      setError('Invalid JSON: ' + err.message);
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonText(formatted);
      setIsFormatted(true);
      setError('');
      onChange(parsed);
    } catch (err) {
      setError('Cannot format invalid JSON: ' + err.message);
    }
  };

  const resetToDefault = () => {
    const formattedJson = JSON.stringify(defaultTemplate, null, 2);
    setJsonText(formattedJson);
    onChange(defaultTemplate);
    setError('');
  };

  const addEndpoint = () => {
    try {
      const current = JSON.parse(jsonText);
      if (!current.app_integration_endpoints) {
        current.app_integration_endpoints = [];
      }
      
      const newEndpoint = {
        route: 'new/route',
        route_type: 'api',
        api_instructions: {
          header: {},
          body: {},
          api_key: ''
        },
        method: 'POST',
        service_name: 'new_service'
      };
      
      current.app_integration_endpoints.push(newEndpoint);
      const formatted = JSON.stringify(current, null, 2);
      setJsonText(formatted);
      onChange(current);
      setError('');
    } catch (err) {
      setError('Cannot add endpoint to invalid JSON');
    }
  };

  const validateEndpoints = () => {
    try {
      const parsed = JSON.parse(jsonText);
      
      if (!parsed.app_integration_endpoints) {
        return 'Missing required field: app_integration_endpoints';
      }
      
      if (!Array.isArray(parsed.app_integration_endpoints)) {
        return 'app_integration_endpoints must be an array';
      }
      
      for (let i = 0; i < parsed.app_integration_endpoints.length; i++) {
        const endpoint = parsed.app_integration_endpoints[i];
        
        if (!endpoint.route) {
          return `Endpoint ${i + 1}: Missing required field 'route'`;
        }
        
        if (!endpoint.route_type) {
          return `Endpoint ${i + 1}: Missing required field 'route_type'`;
        }
        
        if (!['api', 'webhook'].includes(endpoint.route_type)) {
          return `Endpoint ${i + 1}: route_type must be 'api' or 'webhook'`;
        }
        
        if (!endpoint.method) {
          return `Endpoint ${i + 1}: Missing required field 'method'`;
        }
        
        if (!['GET', 'POST', 'PUT', 'DELETE'].includes(endpoint.method)) {
          return `Endpoint ${i + 1}: method must be GET, POST, PUT, or DELETE`;
        }
        
        if (!endpoint.service_name) {
          return `Endpoint ${i + 1}: Missing required field 'service_name'`;
        }
        
        if (endpoint.service_name !== endpoint.service_name.toLowerCase() || 
            endpoint.service_name.includes(' ')) {
          return `Endpoint ${i + 1}: service_name must be lowercase with no spaces`;
        }
      }
      
      return null;
    } catch (err) {
      return 'Invalid JSON format';
    }
  };

  const validationError = validateEndpoints();

  return (
    <div className="platform-endpoints-editor">
      <div className="d-flex align-items-center mb-3">
        <h6 className="mb-0">
          App Integration Endpoints 
          <span className="text-muted ms-2">(Optional)</span>
        </h6>
        <button
          type="button"
          className="btn btn-sm btn-link ms-auto"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <i className={`ti-angle-${isExpanded ? 'up' : 'down'} me-1`}></i>
          {isExpanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {isExpanded && (
        <div className="endpoints-editor-content">
          <div className="alert alert-info mb-3">
            <i className="ti-info-alt me-2"></i>
            Define JSON configuration for API endpoints that will be sent back to your application.
            These endpoints can be used for webhooks, API integrations, or status checks.
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label mb-0">JSON Configuration</label>
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={formatJson}
                  disabled={disabled}
                >
                  <i className="ti-wand me-1"></i>
                  Format
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={addEndpoint}
                  disabled={disabled}
                >
                  <i className="ti-plus me-1"></i>
                  Add Endpoint
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetToDefault}
                  disabled={disabled}
                >
                  <i className="ti-reload me-1"></i>
                  Reset
                </button>
              </div>
            </div>

            <textarea
              className={`form-control font-monospace ${error || validationError ? 'is-invalid' : ''}`}
              rows="15"
              value={jsonText}
              onChange={handleJsonChange}
              disabled={disabled}
              style={{
                fontSize: '0.875rem',
                lineHeight: '1.5',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace'
              }}
            />

            {error && (
              <div className="invalid-feedback d-block">
                <i className="ti-alert me-1"></i>
                {error}
              </div>
            )}

            {!error && validationError && (
              <div className="text-warning small mt-2">
                <i className="ti-alert me-1"></i>
                Validation warning: {validationError}
              </div>
            )}
          </div>

          <div className="endpoints-preview">
            <h6 className="mb-2">Endpoint Summary</h6>
            {(() => {
              try {
                const parsed = JSON.parse(jsonText);
                const endpoints = parsed.app_integration_endpoints || [];
                
                if (endpoints.length === 0) {
                  return (
                    <div className="text-muted small">
                      No endpoints configured
                    </div>
                  );
                }
                
                return (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Route</th>
                          <th>Type</th>
                          <th>Method</th>
                        </tr>
                      </thead>
                      <tbody>
                        {endpoints.map((endpoint, idx) => (
                          <tr key={idx}>
                            <td>
                              <code className="text-primary">{endpoint.service_name || 'N/A'}</code>
                            </td>
                            <td>
                              <code>{endpoint.route || 'N/A'}</code>
                            </td>
                            <td>
                              <span className={`badge bg-${endpoint.route_type === 'webhook' ? 'warning' : 'info'}`}>
                                {endpoint.route_type || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge bg-${
                                endpoint.method === 'GET' ? 'success' :
                                endpoint.method === 'POST' ? 'primary' :
                                endpoint.method === 'PUT' ? 'warning' :
                                endpoint.method === 'DELETE' ? 'danger' : 'secondary'
                              }`}>
                                {endpoint.method || 'N/A'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              } catch (err) {
                return null;
              }
            })()}
          </div>

          <div className="form-text">
            <strong>Structure:</strong>
            <ul className="mb-0 mt-1">
              <li><code>route</code>: API endpoint path</li>
              <li><code>route_type</code>: Either 'api' or 'webhook'</li>
              <li><code>api_instructions</code>: Headers, body, and API key configuration</li>
              <li><code>method</code>: HTTP method (GET, POST, PUT, DELETE)</li>
              <li><code>service_name</code>: Lowercase service identifier without spaces</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformEndpointsEditor;